import React, { useEffect, useState } from 'react';
import { Button, Checkbox, FormControlLabel, Grid, Modal, NativeSelect, Select } from '@material-ui/core';

import { infoTechApi as api } from 'api/infoTech';
import { DataTable, FormComponent, Notifications } from 'shared';

// The table to show roles
function ViewJalaProfileTable({ accountType, parentRow, setExpandedRow }) {
    const [profileID, setProfileID] = useState(parentRow.original.id);
    const [rows, setRows] = useState([]);
    const [addRoleModalOpen, setAddRoleModalOpen] = useState(false);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [roles, setRoles] = useState([]);

    const getRoles = async () => {
        const response = await api.getRoles(accountType);
        if (response.status === false) {
            setNotification({ text: `No roles ${response.msg}`, level: 'error' });
            setRoles([]);
            return;
        }

        const rolesAsOpts = response.data.rows.map((row) => {
            row.text = row.description;
            row.value = row.id;
            return row;
        });

        setRoles(rolesAsOpts);
    };

    const columns = [
        { accessor: 'id', Header: 'RoleID' },
        { accessor: 'description', Header: 'Name' },
        { accessor: 'longDescription', Header: 'Description' },
        {
            accessor: 'remove',
            Header: 'Remove',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                <div
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                >
                    <Button
                        onClick={async () => {
                            const response = await api.deleteRoleFromProfile(profileID, row.original.id);

                            if (response.status === false) {
                                let message = 'Error removing role';
                                if (response.msg) {
                                    message += `: ${response.msg}`;
                                }
                                setNotification({ text: message, level: 'error' });
                                return;
                            }

                            getItemsInTable();
                        }}
                    >
                        REMOVE
                    </Button>
                </div>
            ),
        },
    ];

    let getItemsInTable = async () => {
        const response = await api.getProfileRoles(profileID);

        if (response.status === false) {
            setNotification({ text: `No roles found ${response.msg}`, level: 'error' });
            setRows([]);
            return;
        }

        setRows(response.data.rows);
    };

    useEffect(() => {
        getItemsInTable();
        getRoles();
    }, []);

    return (
        <div>
            <Notifications options={notification} />
            {addRoleModalOpen && (
                <AddRoleModal
                    profileID={profileID}
                    accountType={accountType}
                    closeModal={() => {
                        setAddRoleModalOpen(false);
                        getItemsInTable();
                    }}
                    refreshRoles={getItemsInTable}
                    roles={roles.filter((item) => !rows.map((row) => row.id).includes(item.value))}
                />
            )}
            <DataTable columns={columns} data={rows} />
            <Button
                onClick={() => {
                    setAddRoleModalOpen(true);
                }}
            >
                Add Role
            </Button>
        </div>
    );
}

// The modal to add roles to a profile
function AddRoleModal({ closeModal, profileID, refreshRoles, roles }) {
    const [error, setError] = useState(null);

    const formFields = [
        {
            name: 'roleID',
            inputElement: 'autocomplete',
            label: 'Role',
            gridValue: 6,
            inputProps: { required: true, opts: [{ text: 'Please Select', value: 0 }, ...roles] },
        },
    ];

    return (
        <Modal open onClose={closeModal}>
            <div style={{ outline: 0, backgroundColor: 'white', width: '500px', margin: '10% auto', padding: '10px' }}>
                <FormComponent
                    formFields={formFields}
                    onSubmit={async (formData) => {
                        const response = await api.addRoleToProfile(profileID, formData);
                        if (response.status === false) {
                            let message = 'Error adding item';
                            if (response.msg) {
                                message += `: ${response.msg}`;
                            }
                            setError(`Error updating ${message}`);
                            return;
                        }

                        refreshRoles();
                    }}
                />
                {error && <div style={{ color: 'red' }}>{error}</div>}
            </div>
        </Modal>
    );
}

function ViewJalapenoProfiles({ accountType = 'MISFITS MARKET' }) {
    const [rows, setRows] = useState([]);
    const [activeOnly, setActiveOnly] = useState(true);
    const [approvers, setApprovers] = useState([]);
    const [notification, setNotification] = useState({ text: null, level: null });

    // Load  ttable
    const getRows = async (activeFlag) => {
        const response = await api.getJalapenoProfiles(activeFlag, accountType);
        if (response.status === false) {
            setNotification({ text: `No profiles found ${response.msg}`, level: 'error' });
            setRows([]);
            return;
        }

        if (response.data.rows) {
            response.data.rows.map((row) => {
                row.approversWithNames = row.approvers;
                row.approvers = row.approvers.map((item) => item.id);
                return row;
            });
        }

        setRows(response.data.rows);
    };

    const getProfileApprovers = async () => {
        const response = await api.getProfileApprovers();
        if (!response.status) {
            setNotification({ text: 'Failed to get approvers', level: 'error' });
            return;
        }

        const approverOpts = response.data.rows.map((row) => ({ text: row.name, value: row.id }));
        setApprovers(approverOpts);
    };

    useEffect(() => {
        getRows(activeOnly);
        getProfileApprovers();
    }, []);

    const columns = [
        { accessor: 'id', Header: 'ID' },
        {
            accessor: 'name',
            Header: 'Name',
            editable: true,
            editProps: { type: 'input', inputType: 'text', required: true },
        },
        {
            accessor: 'description',
            Header: 'Description',
            editable: true,
            editProps: { type: 'input', inputType: 'text', required: true },
        },
        {
            accessor: 'approvers',
            Header: 'Approvers',
            editable: true,
            filterValues: (row) => row.original.approversWithNames.map((item) => item.name),
            editProps: {
                type: 'autocomplete-multiple',
                options: approvers,
                multiple: true,
            },
        },
        { accessor: 'active', Header: 'Active', type: 'checkbox', editable: true, editProps: { type: 'checkbox' } },
    ];

    const formFields = [
        { name: 'name', inputElement: 'textField', label: 'Name', gridValue: 6, inputProps: { required: true } },
        {
            name: 'description',
            inputElement: 'textField',
            label: 'Description',
            gridValue: 6,
            inputProps: { required: true },
        },
    ];

    return (
        <div>
            <h1>Profiles</h1>
            <Notifications options={notification} />
            {}
            <div>
                <Grid item xs={12}>
                    <FormComponent
                        formFields={formFields}
                        onSubmit={async (formData, resetForm) => {
                            formData.accountType = accountType;
                            const response = await api.createProfile(formData);
                            if (response.status === false) {
                                let message = 'Error creating profile';
                                if (response.msg) {
                                    message += `: ${response.msg}`;
                                }
                                setNotification({ text: message, level: 'error' });
                                return;
                            }
                            getRows(activeOnly);
                            resetForm();
                        }}
                    />
                </Grid>
                <br />
                <Grid item xs={12}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                id="ActiveOnly_checkbox"
                                onChange={(event) => {
                                    setActiveOnly(event.target.checked);
                                    getRows(event.target.checked);
                                }}
                                color="primary"
                                defaultChecked={activeOnly}
                            />
                        }
                        label="Active Only"
                    />
                </Grid>
                <DataTable
                    columns={columns}
                    data={rows}
                    editable
                    saveEdit={async ({ field, row, value }) => {
                        const update = {};
                        update[field] = value;

                        const res = await api.updateProfile(row.id, update);
                        if (!res.status) {
                            setNotification({ text: res.msg, level: 'error' });
                            return false;
                        }

                        setNotification({ text: 'Profile updated', level: 'success' });
                        getRows(activeOnly);
                        return true;
                    }}
                    expandable
                    ExpansionComponent={<ViewJalaProfileTable accountType={accountType} />}
                />
            </div>
        </div>
    );
}

export { ViewJalapenoProfiles };
